class QuickViewModal extends HTMLElement {
  constructor() {
    super();

    // Caches so repeat opens of the same product are instant and hover-prefetch
    // + click don't both fire a duplicate request.
    this.productCache = new Map();
    this.buttonCache = new Map();
    this.pendingProductFetch = new Map();
    this.pendingButtonFetch = new Map();

    this.currentHandle = null;
    this.currentVariantId = null;
  }

  connectedCallback() {
    this.querySelectorAll('[data-quick-view-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('quick-view-modal--open')) {
        this.close();
      }
    });

    this.querySelector('.quick-view-modal__add-to-cart').addEventListener('click', () => this.addToCart());
  }

  // Called on hover/focus — starts the fetches without opening the modal,
  // so by the time the user actually clicks, the data may already be cached.
  prefetch(handle) {
    if (!handle) return;
    this.fetchProduct(handle).catch(() => {});
    this.fetchPaymentButton(handle).catch(() => {});
  }

  open(handle) {
    if (!handle) return;

    this.currentHandle = handle;
    this.classList.add('quick-view-modal--open', 'quick-view-modal--loading');
    this.classList.remove('quick-view-modal--error');
    document.body.classList.add('quick-view-modal-open-lock');
    this.setAttribute('aria-hidden', 'false');

    Promise.all([this.fetchProduct(handle), this.fetchPaymentButton(handle)])
      .then(([product, extras]) => {
        // If the user opened a different product while this was in flight, drop it.
        if (this.currentHandle !== handle) return;
        this.render(product, extras);
      })
      .catch((error) => {
        if (this.currentHandle !== handle) return;
        console.error(error);
        this.renderError();
      });
  }

  close() {
    this.classList.remove('quick-view-modal--open');
    document.body.classList.remove('quick-view-modal-open-lock');
    this.setAttribute('aria-hidden', 'true');
    this.currentHandle = null;
  }

  fetchProduct(handle) {
    if (this.productCache.has(handle)) {
      return Promise.resolve(this.productCache.get(handle));
    }
    if (this.pendingProductFetch.has(handle)) {
      return this.pendingProductFetch.get(handle);
    }

    const request = fetch(`/products/${handle}.js`)
      .then((response) => {
        if (!response.ok) throw new Error(`Product fetch failed: ${response.status}`);
        return response.json();
      })
      .then((product) => {
        this.productCache.set(handle, product);
        this.pendingProductFetch.delete(handle);
        return product;
      })
      .catch((error) => {
        this.pendingProductFetch.delete(handle);
        throw error;
      });

    this.pendingProductFetch.set(handle, request);
    return request;
  }

  fetchPaymentButton(handle) {
    if (this.buttonCache.has(handle)) {
      return Promise.resolve(this.buttonCache.get(handle));
    }
    if (this.pendingButtonFetch.has(handle)) {
      return this.pendingButtonFetch.get(handle);
    }

    const request = fetch(`/products/${handle}?section_id=quick-view-payment-button`)
      .then((response) => {
        if (!response.ok) throw new Error(`Payment button fetch failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const buttonFragment = parsed.querySelector('.quick-view__payment-button');
        const ratingFragment = parsed.querySelector('.quick-view__rating');
        const result = {
          buttonHtml: buttonFragment ? buttonFragment.outerHTML : '',
          ratingHtml: ratingFragment ? ratingFragment.outerHTML : ''
        };
        this.buttonCache.set(handle, result);
        this.pendingButtonFetch.delete(handle);
        return result;
      })
      .catch((error) => {
        this.pendingButtonFetch.delete(handle);
        throw error;
      });

    this.pendingButtonFetch.set(handle, request);
    return request;
  }

  render(product, extras) {
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const image = product.featured_image || (product.images && product.images[0]);

    const imageEl = this.querySelector('.quick-view-modal__image');
    imageEl.src = this.getSizedImageUrl(image, 800);
    imageEl.alt = product.title;

    this.querySelector('.quick-view-modal__title').textContent = product.title;
    this.querySelector('.quick-view-modal__price').textContent = this.formatMoney(variant.price);
    this.querySelector('.quick-view-modal__description').textContent =
      this.truncate(this.stripHtml(product.description), 220);

    // Server-rendered, same as the homepage card — no JS-side widget refresh needed.
    const ratingEl = this.querySelector('.quick-view-modal__rating');
    ratingEl.innerHTML = '';
    if (extras.ratingHtml) {
      this.setInnerHTML(ratingEl, extras.ratingHtml);
    }

    const paymentButtonContainer = this.querySelector('.quick-view-modal__payment-button');
    paymentButtonContainer.innerHTML = '';
    if (extras.buttonHtml) {
      this.setInnerHTML(paymentButtonContainer, extras.buttonHtml);
    }

    this.querySelector('.quick-view-modal__view-full').href = `/products/${product.handle}`;

    this.currentVariantId = variant.id;
    this.classList.remove('quick-view-modal--loading', 'quick-view-modal--error');
  }

  renderError() {
    this.classList.remove('quick-view-modal--loading');
    this.classList.add('quick-view-modal--error');
    this.querySelector('.quick-view-modal__description').textContent =
      'Sorry, this item could not be loaded. Please try again or visit the product page directly.';
  }

  addToCart() {
    if (!this.currentVariantId) return;

    const button = this.querySelector('.quick-view-modal__add-to-cart');
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: this.currentVariantId,
        quantity: 1,
        // Ask Shopify to render these sections in the response — mini-cart's
        // own renderContents() expects this shape and will update them itself.
        sections: ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    })
      .then((response) => {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then((cartResponse) => {
        button.textContent = originalText;
        button.disabled = false;
        this.close();

        const miniCart = document.querySelector('mini-cart');
        if (miniCart && typeof miniCart.renderContents === 'function') {
          // This updates the drawer's contents AND opens it — no extra call needed.
          miniCart.renderContents(cartResponse);
        } else {
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }
      })
      .catch((error) => {
        console.error(error);
        button.textContent = 'Error — try again';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);
      });
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';
    return url.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, `_${size}x$1$2`);
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try {
      return new Intl.NumberFormat(navigator.language || 'en-GB', {
        style: 'currency',
        currency
      }).format(amount);
    } catch (e) {
      return `£${amount.toFixed(2)}`;
    }
  }

  stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
  }

  truncate(text, length) {
    if (!text || text.length <= length) return text || '';
    return `${text.slice(0, length).trim()}…`;
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects script tags so they execute — required for Shopify's
    // dynamic checkout button script to run.
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view-modal', QuickViewModal);

// Event delegation — works for every product card on the page without
// needing a listener attached per card.
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('.quick-view-trigger');
  if (!trigger) return;
  event.preventDefault();
  const modal = document.querySelector('quick-view-modal');
  if (modal) modal.open(trigger.dataset.productHandle);
});

// Hover/focus prefetch — gives desktop users a head start before they click.
['mouseover', 'focusin'].forEach((eventName) => {
  document.addEventListener(eventName, (event) => {
    const trigger = event.target.closest('.quick-view-trigger');
    if (!trigger) return;
    const modal = document.querySelector('quick-view-modal');
    if (modal) modal.prefetch(trigger.dataset.productHandle);
  });
});